import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useLocation, matchPath } from "react-router-dom";
// @mui
import { Collapse } from "@mui/material";
// hooks
import useActiveLink from "../../../hooks/useActiveLink";
//
import NavItem from "./NavItem";

// ----------------------------------------------------------------------

NavList.propTypes = {
  data: PropTypes.object,
  depth: PropTypes.number,
  hasChild: PropTypes.bool,
};

// Recursive function to check if any descendant (at any depth) is active
function isAnyDescendantActive(children, pathname) {
  if (!children || children.length === 0) return false;
  
  return children.some(child => {
    // Check if this child's path matches the current pathname
    const childActive = child.path ? !!matchPath({ path: child.path, end: false }, pathname) : false;
    
    // Check if this child is active
    if (childActive) return true;
    
    // Recursively check grandchildren and deeper descendants
    if (child.children) {
      return isAnyDescendantActive(child.children, pathname);
    }
    return false;
  });
}

export default function NavList({ data, depth, hasChild }) {
  const { pathname } = useLocation();

  const { active, isExternalLink } = useActiveLink(data.path);

  // Check if any descendant (child, grandchild, etc.) is active recursively
  const isAnyDescendantActiveValue = hasChild && isAnyDescendantActive(data.children, pathname);

  // Keep submenu open if parent is active or any descendant is active
  const shouldBeOpen = active || isAnyDescendantActiveValue;

  const [open, setOpen] = useState(shouldBeOpen);

  useEffect(() => {
    // Only close if neither parent nor any descendant is active
    if (!shouldBeOpen) {
      setOpen(false);
    } else {
      // Keep open if parent or any descendant is active
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, shouldBeOpen]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <NavItem
        item={data}
        depth={depth}
        open={open}
        active={active || isAnyDescendantActiveValue}
        isExternalLink={isExternalLink}
        onClick={handleToggle}
      />

      {hasChild && (
        <Collapse in={open} unmountOnExit>
          <NavSubList data={data.children} depth={depth} />
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

NavSubList.propTypes = {
  data: PropTypes.array,
  depth: PropTypes.number,
};

function NavSubList({ data, depth }) {
  return (
    <>
      {data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
        />
      ))}
    </>
  );
}
